
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { Mutation, State, Action, Getter } from 'vuex-class';
import _groupBy from 'lodash.groupby';
import RequirePasscode from '~/components/Wallet/RequirePasscode.vue';
import FullScreenDialog from '~/components/FullScreenDialog.vue';
import TwoFactorAuthInputPrompt from '~/components/ModalPrompts/TwoFactorAuthInputPrompt.vue';
import exchangeGyriTokenMutation from '~/mutations/exchangeGyriToken.gql';
import bulkHasTransferLockQuery from '~/queries/bulkHasTransferLock.gql';
import AuthQuery from '~/mixins/AuthQuery';
import { ActionType, IGyriUserItem, IBulkLockedItem } from '~/types/user-items';
import { TwoFaCheckpoint } from '~/types/two-fa-checkpoints';
import { EExchangeState, IExchangeReward } from '~/store/customReveal/types';
import { ChainNetwork } from '~/types/chain';
import { SanityDocumentSchema } from '~/types/sanity-documents';
import CustomExchangeReveal from '~/components/Items/CustomExchangeReveal.vue';
import { ICreateWalletPrompt, WalletType } from '~/store/types';

@Component({
  components: {
    RequirePasscode,
    FullScreenDialog,
    CustomExchangeReveal,
    TwoFactorAuthInputPrompt,
  },
})
export default class CustomExchangeRevealUserItem extends Mixins(AuthQuery) {
  @Prop(Object) readonly item!: IGyriUserItem;

  @State((inventory) => inventory.userItems, { namespace: 'inventory' })
  readonly userItems!: IGyriUserItem[];

  @Getter('currentExchangeState', { namespace: 'customReveal' })
  readonly exchangeState!: EExchangeState;

  @Action('setExchangeState', { namespace: 'customReveal' })
  private setExchangeState!: (payload: EExchangeState) => void;

  @Action('setRewards', { namespace: 'customReveal' })
  private setRewards!: (payload: IExchangeReward[]) => void;

  @Action('setVideoReady', { namespace: 'customReveal' })
  private setVideoReady!: (payload: boolean) => void;

  @Action('setVideoBoxType', { namespace: 'customReveal' })
  private setVideoBoxType!: (payload: string) => void;

  @Mutation toggleErrorSnackbar!: (payload?: boolean) => void;
  @Mutation updateSnackbarErrorText!: (args: string) => void;

  @Mutation('toggleCreateWalletPrompt')
  private toggleCreateWalletPrompt!: (
    payload?: Partial<ICreateWalletPrompt>,
  ) => void;

  encryptionPasscode = '';
  selectedItem: IGyriUserItem | null = null;
  similarItems: IGyriUserItem[] = [];
  isLoadingSimilarItems = false;
  noItemsText = 'You have no items to open.';

  async created() {
    this.$watch(
      'selectedItem',
      (newVal: IGyriUserItem | null, oldVal: IGyriUserItem | null) => {
        if (newVal?.exchangeRevealVideo === oldVal?.exchangeRevealVideo) {
          return;
        }
        this.$nextTick(() => {
          this.setVideoReady(false);
          this.setVideoBoxType(this.selectedItem?.exchangeRevealVideo ?? '');
        });
      },
    );
    this.selectedItem = this.item;
    this.getSimilarItemsToExchange();
  }

  @Watch('formattedSimilarItems', { immediate: true })
  onSimilarItemsChange(
    current?: Record<string, string>[],
    previous?: Record<string, string>[],
  ) {
    if (!!previous?.length && current?.length === 0) {
      this.noItemsText = 'You have no more items to open.';
    }
  }

  closeExchange(e: Event) {
    this.$emit('close');
  }

  closeDialog(e: Event) {
    this.setExchangeState(EExchangeState.NOT_EXCHANGING);
  }

  moveObjectToFront(
    arr: IGyriUserItem[],
    key: keyof IGyriUserItem,
    value: string,
  ) {
    const index = arr.findIndex((obj) => obj[key] === value);

    if (index !== -1) {
      const objectToMove = arr[index];
      arr.splice(index, 1);
      arr.unshift(objectToMove);
    }
    return arr;
  }

  resetBox() {
    this.loadNextItem();
  }

  isGyriExchangeItem(item: IGyriUserItem) {
    return (
      !!item.gyriTokenClassKey &&
      !!item.exchangeRevealVideo &&
      !!item.gyriExchanges?.length
    );
  }

  isSimilarItem(item: IGyriUserItem) {
    return (
      item.gyriTokenClassKey?.category ===
        this.selectedItem?.gyriTokenClassKey?.category &&
      item.gyriTokenClassKey?.collection ===
        this.selectedItem?.gyriTokenClassKey?.collection
    );
  }

  async getSimilarItemsToExchange() {
    const raritySortKey: { [key: string]: number } = {
      Ancient: 1,
      Legendary: 2,
      Epic: 3,
      Rare: 4,
      Uncommon: 5,
      Common: 6,
    };

    this.isLoadingSimilarItems = true;
    const similarItems = this.userItems.filter(
      (item) => this.isGyriExchangeItem(item) && this.isSimilarItem(item),
    );

    // Bucket the items into groups of 49 to avoid hitting the 50 item limit which causes an error
    const bucketed = similarItems.reduce((resultArray, item, index) => {
      const bucketIndex = Math.floor(index / 49);
      if (!resultArray[bucketIndex]) {
        resultArray[bucketIndex] = [];
      }
      resultArray[bucketIndex].push(item);
      return resultArray;
    }, [] as IGyriUserItem[][]);

    const tokenLockData = new Map<string, IBulkLockedItem>();

    Promise.all(
      bucketed.map((bucket) =>
        this.getTokenLockResults(bucket).catch((err) => console.error(err)),
      ),
    ).then((bucketedResponse) => {
      bucketedResponse?.forEach((bucket) => {
        bucket?.forEach((item) => {
          if (
            item?.tokenInstanceId &&
            !tokenLockData.has(item.tokenInstanceId)
          ) {
            tokenLockData.set(item.tokenInstanceId, item);
          }
        });
      });
    });

    const filteredSimilarItems = similarItems.filter((item) => {
      const itemInstanceId = `${item.sendId}|${item.nonFungibleInstanceId}`;
      const canExchangeIfLocked = tokenLockData
        .get(itemInstanceId)
        ?.allowedActions?.includes(ActionType.EXCHANGE);
      return !tokenLockData.has(itemInstanceId) || canExchangeIfLocked;
    });

    this.similarItems = filteredSimilarItems.sort(
      (a, b) => raritySortKey[a.rarity.label] - raritySortKey[b.rarity.label],
    );
    this.isLoadingSimilarItems = false;
  }

  async getTokenLockResults(items: IGyriUserItem[]) {
    const tokenInstances = items.map((instance) => {
      const { collection, category, type, additionalKey } =
        instance.gyriTokenClassKey;
      return {
        instance: instance.fungible ? '0' : instance.nonFungibleInstanceId,
        collection,
        category,
        type,
        additionalKey,
      };
    });

    const tokenLockResponse = await this.$apollo.query<{
      bulkHasTransferLock?: IBulkLockedItem[];
    }>({
      query: bulkHasTransferLockQuery,
      variables: { tokenInstances },
      fetchPolicy: 'no-cache',
    });
    return tokenLockResponse?.data?.bulkHasTransferLock ?? [];
  }

  async exchangeToken({
    exchangeId,
    walletPassword,
    tokens,
    totpToken,
  }: {
    exchangeId: number;
    walletPassword: string;
    totpToken: string;
    tokens: Array<{
      collection: string;
      category: string;
      type: string;
      additionalKey: string;
      instance: string;
    }>;
  }): Promise<{
    exchangeNetwork: ChainNetwork.GYRI;
    response: { rewards?: IExchangeReward[] };
  }> {
    const res = await this.$apollo.mutate<{
      exchangeGyriToken?: { rewards?: IExchangeReward[] };
    }>({
      mutation: exchangeGyriTokenMutation,
      variables: {
        exchangeId,
        exchangeTokens: tokens.map(
          ({ collection, category, type, additionalKey, instance }) => ({
            tokenInstanceKey: {
              collection,
              category,
              type,
              additionalKey,
              instance,
            },
            quantity: '1',
          }),
        ),
        walletPassword,
        totpToken,
      },
    });

    if (res?.data?.exchangeGyriToken) {
      return {
        exchangeNetwork: ChainNetwork.GYRI,
        response: res?.data?.exchangeGyriToken,
      };
    }

    throw new Error('Something went wrong');
  }

  async onExchangeClick() {
    if (this.missingWallet) {
      this.showCreateWalletModal();
      return;
    }

    if (!this.encryptionPasscode) {
      this.setExchangeState(EExchangeState.ENTER_PASSWORD);
      return;
    } else {
      try {
        const res = await this.exchangeItems(this.encryptionPasscode);
        if (res) {
          this.onExchangeComplete(res);
        } else {
          throw new Error('Something went wrong');
        }
      } catch (error) {
        console.warn(error);
        this.onExchangeError({
          message: (error as Error).message,
        });
      }
    }
  }

  showCreateWalletModal() {
    const action = this.$t('pages.inventory.nfts.item.exchange') as string;
    const actionLabel = action.toLowerCase();
    const itemName = this.item?.fullName ?? this.item?.name;
    const bodyText = !actionLabel
      ? ''
      : this.missingWallet === WalletType.ETH
      ? (itemName
          ? this.$t('pages.inventory.nfts.item.setupWeb3WalletWithName', {
              action: actionLabel,
              name: itemName,
            })
          : this.$t('pages.inventory.nfts.item.setupWeb3Wallet', {
              action: actionLabel,
            })
        ).toString()
      : (itemName
          ? this.$t('pages.inventory.nfts.item.setupGyriWalletWithName', {
              action: actionLabel,
              name: itemName,
            })
          : this.$t('pages.inventory.nfts.item.setupGyriWallet', {
              action: actionLabel,
            })
        ).toString();

    this.toggleCreateWalletPrompt({
      show: true,
      walletType: this.missingWallet,
      body: bodyText,
    });
  }

  exchangeItems(walletPassword: string) {
    const item = this.selectedItem;

    if (
      !item ||
      !this.tokenId ||
      item.network !== ChainNetwork.GYRI ||
      !walletPassword
    ) {
      return;
    }
    this.encryptionPasscode = walletPassword;
    this.$emit('exchange');
    this.setExchangeState(EExchangeState.EXCHANGE_IN_FLIGHT);
    return this.doAuthQuery(
      async (totpToken) => {
        const data = await this.exchangeToken({
          exchangeId: item.gyriExchanges[0].id,
          walletPassword,
          totpToken,
          tokens: [
            {
              collection: item.gyriTokenClassKey.collection,
              category: item.gyriTokenClassKey.category,
              type: item.gyriTokenClassKey.type,
              additionalKey: item.gyriTokenClassKey.additionalKey,
              instance: item.nonFungibleInstanceId,
            },
          ],
        });

        return data;
      },
      TwoFaCheckpoint.transactions,
      { hideDialogDuringQuery: true },
    );
  }

  onExchangeComplete(res: {
    exchangeNetwork: ChainNetwork.ETHEREUM | ChainNetwork.GYRI;
    response: { rewards?: IExchangeReward[] };
  }) {
    this.setRewards(res.response.rewards ?? []);
    this.setExchangeState(EExchangeState.EXCHANGE_COMPLETE);
    this.$emit('exchange-complete');
  }

  onExchangeError(res: { message: string }) {
    this.encryptionPasscode = '';
    this.setExchangeState(EExchangeState.NOT_EXCHANGING);
    this.$emit('exchange-error', res.message);
    this.updateSnackbarErrorText(res.message);
    this.toggleErrorSnackbar();
  }

  loadNextItem() {
    if (this.similarItems.length > 1 && this.selectedItem) {
      const indexToRemove = this.similarItems.findIndex(
        (item) =>
          item.name === this.selectedItem?.name &&
          item.nonFungibleInstanceId ===
            this.selectedItem?.nonFungibleInstanceId,
      );

      this.similarItems.splice(indexToRemove, 1);

      this.selectedItem = this.similarItems[0];
    } else {
      this.selectedItem = null;
      this.similarItems = [];
    }
    this.setExchangeState(EExchangeState.NOT_EXCHANGING);
  }

  get buyMoreLink() {
    return this.selectedItem?.gyriTokenClassKey?.category
      ? this.$sanityLookup.getPageRoutePath({
          schema: SanityDocumentSchema.PROJECT,
          slug: this.selectedItem?.gyriTokenClassKey?.category,
        })
      : '';
  }

  get shouldShowPasswordModal() {
    return this.exchangeState === EExchangeState.ENTER_PASSWORD;
  }

  get tokenId() {
    return this.selectedItem?.nonFungibleInstanceId;
  }

  get logo() {
    return this.selectedItem?.logo;
  }

  get formattedSimilarItems() {
    const filters = this.$options.filters;
    const firstItem = Array.isArray(this.$route.query.selector)
      ? this.$route.query.selector.find((v) => !!v)
      : this.$route.query.selector;

    return Object.entries(_groupBy(this.similarItems, 'name')).map(
      ([name, items]) => {
        const quantity = items.length;
        const pluralizedName =
          quantity > 1 && filters ? filters.pluralize(name) : name;
        const groupItems = firstItem
          ? this.moveObjectToFront(items, 'uniqueInventoryPath', firstItem)
          : items;

        return {
          item: groupItems[0],
          name: name,
          nameWithQuantity: `${quantity} ${pluralizedName}`,
          image: groupItems[0].icon || groupItems[0].image,
        };
      },
    );
  }

  get missingWallet() {
    return this.$walletHelper.missingWalletTypeByNetwork(this.item?.network);
  }
}
